import { useErrorBoundary } from "component-library";
import { AxiosError } from "axios";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";
import { useQuery } from "react-query";
import { authAxios as axios } from "api-generate-types/src/axios";
import { parseToUrlQuery } from "shared/utils/http";
import { getApiUrl } from "services/UrlBuilder";
import { HALManager, UserManager } from "core";
import CreditsList from "../../../models/Credits/CreditsList";
import { Credit, CreditHeading } from "models";

const GET_CREDITS_QUERY_KEY = "GET_CREDITS_QUERY_KEY";

export function useGetCredits(): {
  creditsHeadings: CreditHeading[];
  credits: Credit[];
  isLoading: boolean;
} {
  const captureError = useErrorBoundary();
  const { isPublicView } = usePublicView();
  const { data: publicProfileData, isLoading: isPublicProfileLoading } =
    useGetPublicProfile();

  const { data: creditsData, isLoading: creditsLoading } = useQuery<
    CreditsList,
    AxiosError
  >(
    GET_CREDITS_QUERY_KEY,
    async () => {
      const urlRequest = getApiUrl(
        HALManager.getCreditsUrl(UserManager.userId),
        parseToUrlQuery({ nocache: new Date().getTime() })
      );
      const { data } = await axios.get(urlRequest, {
        headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
      });

      return data;
    },
    {
      enabled: !isPublicView,
      cacheTime: 0,
      onError: captureError,
    }
  );

  const isLoading = isPublicProfileLoading || creditsLoading;

  const creditsHeadings = isPublicView
    ? (publicProfileData?.credits?.creditHeadings ?? [])
    : (creditsData?.creditHeadings ?? []);

  const credits = (
    isPublicView
      ? (publicProfileData?.credits?.credits ?? [])
      : (creditsData?.credits ?? [])
  )?.map((credit) => ({
    ...credit,
    type: creditsHeadings?.find(
      (heading) => heading.creditType.id === credit.typeId
    )?.creditType.name,
  }));

  return {
    creditsHeadings,
    credits,
    isLoading,
  };
}
